import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { Subject, Observable, timer, of } from 'rxjs';
import { debounceTime, switchMap, takeUntil, take, tap, delay, catchError } from 'rxjs/operators';

/**
 * RxTimingService - Serviço de Timing com RxJS
 * 
 * Substitui o uso de setTimeout/clearTimeout por operadores RxJS,
 * proporcionando melhor controle, cancelamento automático e integração
 * com o ciclo de vida do Angular.
 * 
 * Vantagens:
 * - Cancelamento automático via takeUntil/unsubscribe
 * - Debounce e throttle built-in
 * - Integração com zone.js para detecção de mudanças
 * - Evita memory leaks de timers não cancelados
 * 
 * IMPORTANTE: Este serviço NÃO é global (providedIn: 'root')
 * Ele deve ser fornecido a nível de componente para garantir
 * instâncias isoladas por launcher.
 */
@Injectable()
export class RxTimingService implements OnDestroy {
  
  /** Subject para cancelamento de todas as operações ao destruir */
  private destroy$ = new Subject<void>();
  
  /** Map de debounce subjects por chave */
  private debounceSubjects = new Map<string, Subject<() => void>>();
  
  /** Map de timers ativos para cancelamento manual */
  private activeTimers = new Map<string, Subject<void>>();
  
  constructor(private ngZone: NgZone) {}
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Limpar todos os subjects
    this.debounceSubjects.forEach(subject => subject.complete());
    this.debounceSubjects.clear();
    
    this.activeTimers.forEach(cancel$ => cancel$.next());
    this.activeTimers.clear();
  }

  // ============================================================================
  // Substituições diretas de setTimeout
  // ============================================================================

  /**
   * Executa uma função após um delay (substitui setTimeout simples)
   * @param fn Função a executar
   * @param delayMs Delay em milissegundos
   * @param key Chave opcional para cancelamento
   * @returns Observable que emite quando executa
   */
  runAfterDelay<T = void>(
    fn: () => T,
    delayMs: number,
    key?: string
  ): Observable<T> {
    // Cancelar timer anterior com mesma chave
    if (key && this.activeTimers.has(key)) {
      this.activeTimers.get(key)!.next();
    }
    
    const cancel$ = new Subject<void>();
    if (key) {
      this.activeTimers.set(key, cancel$);
    }
    
    return timer(delayMs).pipe(
      takeUntil(this.destroy$),
      takeUntil(cancel$),
      switchMap(() => {
        // Executar dentro da zone do Angular para trigger change detection
        let result: T;
        this.ngZone.run(() => {
          result = fn();
        });
        return of(result!);
      }),
      tap(() => {
        if (key) {
          this.activeTimers.delete(key);
        }
      }),
      catchError(err => {
        console.error('[RxTiming] Erro em runAfterDelay:', err);
        if (key) {
          this.activeTimers.delete(key);
        }
        throw err;
      })
    );
  }

  /**
   * Cancela um timer ativo pela chave
   * @param key Chave do timer
   */
  cancelTimer(key: string): void {
    const cancel$ = this.activeTimers.get(key);
    if (cancel$) {
      cancel$.next();
      this.activeTimers.delete(key);
    }
  }

  // ============================================================================
  // Debounce com RxJS
  // ============================================================================

  /**
   * Cria um debounce subject para uma chave específica
   * @param key Identificador único do debounce
   * @param delayMs Tempo de debounce em ms
   * @returns Subject que recebe funções para execução debounced
   */
  getDebounceSubject(key: string, delayMs: number): Subject<() => void> {
    if (!this.debounceSubjects.has(key)) {
      const subject = new Subject<() => void>();
      
      subject.pipe(
        takeUntil(this.destroy$),
        debounceTime(delayMs),
        switchMap(fn => {
          return new Observable<void>(observer => {
            this.ngZone.run(() => {
              try {
                fn();
                observer.next(undefined);
                observer.complete();
              } catch (err) {
                observer.error(err);
              }
            });
          });
        })
      ).subscribe();
      
      this.debounceSubjects.set(key, subject);
    }
    
    return this.debounceSubjects.get(key)!;
  }

  /**
   * Executa uma função com debounce
   * @param key Chave do debounce
   * @param fn Função a executar
   * @param delayMs Tempo de debounce em ms
   */
  debounce(key: string, fn: () => void, delayMs: number): void {
    const subject = this.getDebounceSubject(key, delayMs);
    subject.next(fn);
  }

  // ============================================================================
  // Utilitários para flags temporárias (substituir patterns de set/setTimeout/clear)
  // ============================================================================

  /**
   * Define uma flag temporária que limpa automaticamente após delay
   * Útil para substituir patterns como:
   *   flag = true;
   *   setTimeout(() => flag = false, delay);
   * 
   * @param setFn Função para definir a flag (recebe true)
   * @param clearFn Função para limpar a flag (recebe false)
   * @param delayMs Delay antes de limpar
   * @param key Chave opcional para cancelamento
   */
  setTemporaryFlag(
    setFn: (value: boolean) => void,
    clearFn: (value: boolean) => void,
    delayMs: number,
    key?: string
  ): void {
    // Definir flag imediatamente
    this.ngZone.run(() => setFn(true));
    
    // Agendar limpeza
    this.runAfterDelay(() => clearFn(false), delayMs, key)
      .pipe(take(1))
      .subscribe();
  }

  /**
   * Adiciona um item a um Map/Set com limpeza automática
   * Útil para substituir patterns como:
   *   map.set(key, Date.now());
   *   setTimeout(() => map.delete(key), delay);
   * 
   * @param map Map ou Set para manipular
   * @param key Chave/valor a adicionar
   * @param delayMs Delay antes de remover
   * @param timerKey Chave para o timer (opcional)
   */
  addWithAutoRemove<K, V>(
    map: Map<K, V>,
    key: K,
    value: V,
    delayMs: number,
    timerKey?: string
  ): void {
    map.set(key, value);
    
    const effectiveTimerKey = timerKey || `autoRemove_${String(key)}`;
    
    this.runAfterDelay(() => map.delete(key), delayMs, effectiveTimerKey)
      .pipe(take(1))
      .subscribe();
  }

  // ============================================================================
  // Retry com delay exponencial
  // ============================================================================

  /**
   * Executa uma função com retry e delay exponencial
   * @param fn Função a executar (deve retornar true se sucesso)
   * @param maxRetries Número máximo de tentativas
   * @param baseDelayMs Delay base (será multiplicado por tentativa)
   * @param key Chave para cancelamento
   */
  retryWithBackoff(
    fn: () => boolean,
    maxRetries: number = 3,
    baseDelayMs: number = 200,
    key?: string
  ): Observable<boolean> {
    return new Observable<boolean>(observer => {
      let attempt = 0;
      
      const tryExecute = () => {
        this.ngZone.run(() => {
          try {
            const success = fn();
            if (success || attempt >= maxRetries) {
              observer.next(success);
              observer.complete();
            } else {
              attempt++;
              const delayMs = baseDelayMs * attempt;
              this.runAfterDelay(tryExecute, delayMs, key)
                .pipe(take(1))
                .subscribe();
            }
          } catch (err) {
            observer.error(err);
          }
        });
      };
      
      tryExecute();
    }).pipe(
      takeUntil(this.destroy$)
    );
  }

  // ============================================================================
  // Frame-based timing (para operações que precisam esperar o DOM)
  // ============================================================================

  /**
   * Executa uma função no próximo animation frame
   * Útil para operações que precisam esperar o DOM renderizar
   * @param fn Função a executar
   */
  runOnNextFrame(fn: () => void): void {
    requestAnimationFrame(() => {
      this.ngZone.run(fn);
    });
  }

  /**
   * Executa uma função após N animation frames
   * @param fn Função a executar
   * @param frames Número de frames a esperar
   */
  runAfterFrames(fn: () => void, frames: number = 1): void {
    if (frames <= 0) {
      this.ngZone.run(fn);
      return;
    }
    
    requestAnimationFrame(() => {
      this.runAfterFrames(fn, frames - 1);
    });
  }
}
